import React, { useState, useEffect } from 'react';
import { getXeroStatus, getXeroConnectUrl, disconnectXero, pullInvoicesFromXero } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Link2, Link2Off, RefreshCw, CheckCircle2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const XeroSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refresh = () => getXeroStatus().then(setStatus);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const connect = async () => {
    try {
      const { authorize_url } = await getXeroConnectUrl();
      // Open in new tab; when user returns, they can refresh.
      window.open(authorize_url, '_blank', 'noopener,noreferrer');
      toast.info('Complete authorisation in the new tab, then come back and click Refresh.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start Xero connect');
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Xero? Existing synced invoices will keep their references, but no new syncs will run.')) return;
    try {
      await disconnectXero();
      toast.success('Disconnected from Xero');
      await refresh();
    } catch (e) {
      toast.error('Disconnect failed');
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const res = await pullInvoicesFromXero();
      toast.success(`Sync complete: ${res.imported || 0} imported, ${res.updated || 0} updated, ${res.scanned || 0} scanned`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading...</div>;

  const connected = status?.connected;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl">
      <PageHeader
        title="Xero Integration"
        description="Two-way sync with your Xero accounting org for invoices, quotes, and contacts."
      />

      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-md flex items-center justify-center ${connected ? 'bg-green-100' : 'bg-slate-100'}`}>
            {connected ? <CheckCircle2 className="h-6 w-6 text-green-700" /> : <AlertCircle className="h-6 w-6 text-slate-500" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-display uppercase text-lg text-slate-900">{connected ? 'Connected' : 'Not connected'}</h2>
              {connected && (
                <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                  Live
                </span>
              )}
            </div>
            {connected ? (
              <div className="space-y-1 text-sm text-slate-600">
                <div>Organisation: <span className="font-medium text-slate-900" data-testid="xero-tenant-name">{status.tenant_name || status.tenant_id}</span></div>
                <div>Token expires: {new Date(status.expires_at).toLocaleString('en-GB')}</div>
                {status.last_refreshed_at && <div>Last refreshed: {new Date(status.last_refreshed_at).toLocaleString('en-GB')}</div>}
                {/* Surface the granted OAuth scope set. If the
                    `accounting.attachments` scope is missing, the user
                    will hit 401/403 every time we try to push a PDF or
                    receipt image — and the fix is to disconnect and
                    reconnect (Xero re-prompts for consent and we'll ask
                    for attachments). Without this warning, the only
                    symptom is a misleading "expired" toast on every
                    attachment upload. */}
                {status.scope && !status.scope.includes('accounting.attachments') && (
                  <div
                    className="mt-2 bg-amber-50 border border-amber-300 text-amber-900 rounded px-3 py-2 text-xs"
                    data-testid="xero-missing-attachment-scope"
                  >
                    <strong>Attachment scope missing.</strong> This Xero
                    connection was granted before we added receipt-attachment
                    support, so every push currently fails to attach the
                    source PDF / receipt. Click <strong>Disconnect</strong>
                    then <strong>Connect to Xero</strong> again — the
                    consent prompt will ask you to grant attachment access.
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-600">
                Click <strong>Connect to Xero</strong> to authorise Lutrom Engineering to read and write invoices, quotes and contacts in your Xero organisation.
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={refresh} data-testid="xero-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh status
            </Button>
            {connected ? (
              <Button variant="outline" size="sm" onClick={disconnect} data-testid="xero-disconnect-btn">
                <Link2Off className="h-4 w-4 mr-2" /> Disconnect
              </Button>
            ) : (
              <Button onClick={connect} className="bg-slate-900 hover:bg-slate-800" data-testid="xero-connect-btn">
                <Link2 className="h-4 w-4 mr-2" /> Connect to Xero
              </Button>
            )}
          </div>
        </div>
      </div>

      {connected && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-display uppercase text-sm text-slate-900 mb-2">Pull from Xero</h3>
            <p className="text-sm text-slate-600 mb-4">Refresh local invoice statuses (paid, voided, authorised) from Xero. Runs automatically every 30 minutes.</p>
            <Button onClick={syncNow} disabled={syncing} variant="outline" className="w-full" data-testid="xero-sync-now-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync invoices now'}
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-display uppercase text-sm text-slate-900 mb-2">Push to Xero</h3>
            <p className="text-sm text-slate-600 mb-4">New invoices and quotes created in Lutrom can be pushed to Xero on demand from their list pages.</p>
            <a href="/admin/invoices" className="text-sm font-medium text-blue-800 hover:text-blue-900 inline-flex items-center gap-1">
              Go to invoices <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-900">
        <strong>Redirect URI in use:</strong> <code className="bg-white px-1.5 py-0.5 rounded border border-blue-300 break-all">https://portal.lutrom.co.uk/api/xero/callback</code>.
        Make sure this exact URL is listed under <em>Configuration → Redirect URIs</em> in your Xero app and that you've clicked <strong>Save</strong>.
      </div>
    </div>
  );
};

export default XeroSettings;
